export const STATUTES_INDEX_SEO = {
  title: 'Pakistani Statutes & Laws | LexGat',
  description:
    'Browse and search Pakistani statutes online — Constitution of Pakistan 1973, Code of Civil Procedure 1908, Qanun-e-Shahadat Order, and more. Free full-text access for lawyers and researchers.',
  canonical: 'https://lexgat.com/statutes',
};

function extractYear(displayTitle: string): number | null {
  const match = displayTitle.match(/\b(1[89]\d{2}|20\d{2})\b/);
  return match ? parseInt(match[1], 10) : null;
}

export function buildStatuteSeoTitle(displayTitle: string, year?: number | null): string {
  const normalized = displayTitle.trim();
  const lower = normalized.toLowerCase();
  const y = year ?? extractYear(normalized);

  if (lower.includes('constitution')) {
    return `Constitution of Pakistan ${y || '1973'} — Full Text Online | LexGat`;
  }
  if (lower.includes('code of civil procedure') || /\bc\.?\s*p\.?\s*c\.?\b/i.test(normalized)) {
    return 'Code of Civil Procedure 1908 (CPC) — Full Text | LexGat';
  }
  if (lower.includes('qanun') || lower.includes('shahadat') || /\bq\.?\s*s\.?\s*o\.?\b/i.test(normalized)) {
    return 'Qanun-e-Shahadat Order 1984 (QSO) — Full Text | LexGat';
  }
  if (lower.includes('penal code') || lower.includes('ppc')) {
    return `Pakistan Penal Code${y ? ` ${y}` : ''} — Full Text | LexGat`;
  }
  if (y) {
    return `${normalized} — Full Text Online | LexGat`;
  }
  return `${normalized} — Full Text | LexGat`;
}

export function buildStatuteSeoDescription(
  displayTitle: string,
  year?: number | null,
  sectionCount?: number | null,
): string {
  const normalized = displayTitle.trim();
  const lower = normalized.toLowerCase();
  const y = year ?? extractYear(normalized);
  const sectionPhrase = sectionCount
    ? `${sectionCount} searchable section${sectionCount === 1 ? '' : 's'}`
    : 'sections, parts, and orders';

  if (lower.includes('constitution')) {
    return `Read the full text of the Constitution of Pakistan${y ? ` (${y})` : ''}. Browse articles, amendments, and schedules — ${sectionPhrase} with free online access for lawyers and researchers.`;
  }
  if (lower.includes('code of civil procedure') || /\bc\.?\s*p\.?\s*c\.?\b/i.test(normalized)) {
    return `Read the Code of Civil Procedure 1908 (CPC) of Pakistan online. Search ${sectionPhrase}, orders, and rules — free access for Pakistani litigation lawyers.`;
  }
  if (lower.includes('qanun') || lower.includes('shahadat')) {
    return `Read the Qanun-e-Shahadat Order 1984 (QSO) of Pakistan online. Search ${sectionPhrase} on evidence law — free access for Pakistani lawyers.`;
  }
  return `Read the full text of the ${normalized} of Pakistan online. Search ${sectionPhrase}. Free, fast access for Pakistani lawyers and legal researchers.`;
}

export type StatuteFaq = { question: string; answer: string };

export function buildStatuteFaqs(displayTitle: string, year?: number | null): StatuteFaq[] {
  const normalized = displayTitle.trim();
  const lower = normalized.toLowerCase();
  const y = year ?? extractYear(normalized) ?? 1973;

  if (lower.includes('constitution')) {
    return [
      {
        question: `What is the Constitution of Pakistan ${y}?`,
        answer: `The Constitution of Pakistan ${y} is the supreme law of Pakistan. It defines the structure of government, fundamental rights, and the powers of the legislature, executive, and judiciary.`,
      },
      {
        question: `When was the Constitution of Pakistan ${y} enacted?`,
        answer: `The Constitution of Pakistan was adopted in ${y}. It remains the primary constitutional document for the Islamic Republic of Pakistan.`,
      },
      {
        question: 'Can I read the full Constitution of Pakistan online?',
        answer:
          'Yes. LexGat publishes the full searchable text of the Constitution of Pakistan, including articles, parts, schedules, and amendments, for free.',
      },
    ];
  }

  if (lower.includes('code of civil procedure') || /\bc\.?\s*p\.?\s*c\.?\b/i.test(normalized)) {
    return [
      {
        question: 'What is the Code of Civil Procedure 1908 (CPC)?',
        answer:
          'The Code of Civil Procedure 1908 (CPC) governs civil court procedure in Pakistan, including suits, appeals, execution, and related civil proceedings.',
      },
      {
        question: 'Can I read the CPC of Pakistan online?',
        answer:
          'Yes. LexGat provides the full searchable text of the Code of Civil Procedure 1908 for Pakistani lawyers and law students.',
      },
    ];
  }

  return [];
}

export function buildStatuteBreadcrumbJsonLd(displayTitle: string, canonical: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Pakistani Statutes & Laws',
        item: STATUTES_INDEX_SEO.canonical,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: displayTitle,
        item: canonical,
      },
    ],
  };
}

export function buildStatuteFaqJsonLd(faqs: StatuteFaq[]) {
  if (!faqs.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: answer,
      },
    })),
  };
}

export type StatuteSeoEntry = {
  id: string;
  displayTitle: string;
  canonical: string;
  year?: number | null;
  sectionCount?: number | null;
};

export function buildStatuteJsonLdBlocks(statute: StatuteSeoEntry) {
  const faqs = buildStatuteFaqs(statute.displayTitle, statute.year);
  const blocks: object[] = [
    {
      '@context': 'https://schema.org',
      '@type': 'Legislation',
      name: statute.displayTitle,
      url: statute.canonical,
      description: buildStatuteSeoDescription(
        statute.displayTitle,
        statute.year,
        statute.sectionCount,
      ),
      legislationIdentifier: statute.id,
      legislationJurisdiction: 'Pakistan',
      legislationType: 'Act',
      ...(statute.year ? { datePublished: String(statute.year) } : {}),
      inLanguage: 'en',
      publisher: {
        '@type': 'Organization',
        name: 'Government of Pakistan',
      },
      isAccessibleForFree: true,
    },
    buildStatuteBreadcrumbJsonLd(statute.displayTitle, statute.canonical),
  ];
  const faqBlock = buildStatuteFaqJsonLd(faqs);
  if (faqBlock) blocks.push(faqBlock);
  return blocks;
}

const FEATURE_KEYWORDS = ['constitution', 'civil procedure', 'cpc', 'shahadat', 'qanun', 'penal'];

export function pickFeaturedStatutes<T extends { displayTitle: string }>(
  statutes: Record<string, T>,
  limit = 6,
): T[] {
  const entries = Object.values(statutes);
  return entries
    .map((entry) => {
      const lower = entry.displayTitle.toLowerCase();
      const score = FEATURE_KEYWORDS.findIndex((keyword) => lower.includes(keyword));
      return { entry, score: score === -1 ? 999 : score };
    })
    .sort(
      (a, b) => a.score - b.score || a.entry.displayTitle.localeCompare(b.entry.displayTitle),
    )
    .slice(0, limit)
    .map(({ entry }) => entry);
}
